import {gsap, Power2, TimelineLite, TweenMax} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {ReelsView} from "../../../game/reels/ReelsView";
import {Config} from "../../../game/config/Config";
import {Game} from "../../../game/Game";
import {GameFeatureMode} from "../../../game/enums/Enum";


export interface Positionable {
    position: {
        x: number,
        y: number
    }
}

export interface SimpleReelModelSymbolPosition {
    reelID: number;
    position: number
}


export interface SimpleReelModelDelegate {
    createSymbol(symbolName: string, state: SimpleReelModelState, position: SimpleReelModelSymbolPosition): Positionable;

    removeSymbol(symbolData: SimpleReelModelSymbolData, state: SimpleReelModelState): void;
}

export interface SimpleReelModelSymbolData {
    name: string,
    positionable: Positionable,
    reelPosition: number
}

export enum SimpleReelModelState {
    IDLE = "IDLE",
    START = "START",
    SPIN = "SPIN",
    STOP = "STOP",
    STOPPED = "STOPPED",
    BOUNCE = "BOUNCE",
    NUDGE = "NUDGE",
}

export interface SimpleReelModelEventData {
    reel: number;
    state: SimpleReelModelState;
}


export interface SimpleReelPaddingData {
    visualPaddingBefore: number;
    paddingBefore: number;
    visibleSymbols: number;
    paddingAfter: number;
    visualPaddingAfter: number;
}

export class SimpleReelModel {
    public paddingConfig: SimpleReelPaddingData = {
        visualPaddingBefore: 1,
        paddingBefore: 0,
        visibleSymbols: 3,
        paddingAfter: 0,
        visualPaddingAfter: 1,
    }
    public animatedStepData = {
        y: 0
    }
    public stepSize: number = 180;
    public STEP_DURATION: number;
    public readonly id: number;
    public isNudgeReel: boolean = false;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     *
     *
     */
    public readonly events: APIEventSystem | undefined;
    private _stopTriggerTl: TimelineLite | undefined;
    private _reelSet: string[] | undefined;
    private _reelSetIndex: number = 0;
    private _stopIndex: number;
    private _stopSymbols: SimpleReelModelSymbolData[] = [];
    private _symbols: SimpleReelModelSymbolData[] = [];
    private _spinning: boolean = false;
    private _currentSpinStep: gsap.core.Tween | undefined;
    private animationTimeScale = 1;
    private _stopTimeline: TimelineLite | undefined;
    private _stopping: boolean = false;
    private triggerStop: boolean = false;
    private _startedReverse: boolean;
    private _speed: number = this.animationTimeScale / Config.STEP_DURATION;
    private masterSpinTimeline: TimelineLite | undefined;
    private _state: SimpleReelModelState = SimpleReelModelState.IDLE;
    private _reelView: ReelsView;
    private _nudgeSteps: number = 0;

    constructor(id: number, reelsView: ReelsView, triggerEvents: boolean = false) {
        this.id = id;
        this._reelView = reelsView;
        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data: SimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }
    }

    public setReelSet(reelSet: string[], index?: number) {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    public init(symbols: string[]) {
        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);
        const visibleSymbols: number = Config.SYMBOLS_IN_REEL[this.id];
        this.paddingConfig.visibleSymbols = visibleSymbols;
        const totalInitCount = this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }

        symbols = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(symbols);
        symbols = symbols.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(symbols[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.IDLE);
            this._symbols.push(symbol);
            this._stopSymbols.push(symbol);
        }

        this.updatePosition();
    }

    public halt() {
        if (this._currentSpinStep) {
            this.masterSpinTimeline?.kill();
            this._currentSpinStep?.progress(1);
        }
    }

    public resume() {
        this.startSpin();
    }

    public stop(stopSymbolNames: string[], reverse: boolean = false): TimelineLite {
        reverse = this._startedReverse;

        this.clearSymbols(this._stopSymbols);

        this._stopSymbols = [];
        stopSymbolNames = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(stopSymbolNames);
        stopSymbolNames = stopSymbolNames.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));
        for (let i: number = 0; i < stopSymbolNames.length; i++) {
            const symbol = this.createNewSymbol(stopSymbolNames[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.STOP);
            symbol.positionable.position.y = this.stepSize * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
            this._stopSymbols.push(symbol);
        }

        this._stopTriggerTl = new TimelineLite({
            paused: true,
            onStart: () => {
                this.state = SimpleReelModelState.STOP;
                this._spinning = false;
                this.triggerStop = true;
            }
        });

        this._stopTimeline = new TimelineLite({
            paused: true,
            onStart: () => {
                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;
            },
            onComplete: () => {
                this.state = SimpleReelModelState.STOPPED;
                this._stopTimeline = undefined;
                this._stopping = false;
                this.state = SimpleReelModelState.IDLE;
            }
        });

        const numberOfNormalSteps = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step(reverse);
            this._stopTimeline.add(tween);
        }

        this._stopTimeline.add(this.bounce());

        this._stopTriggerTl.add(new gsap.core.Tween(this, this._stopTimeline.totalDuration(), {}));

        return <TimelineLite>this._stopTriggerTl.play(0);
    }

    public start(reverse: boolean = false): TimelineLite {
        this.isNudgeReel = false;
        this._startedReverse = reverse;
        const tl: TimelineLite = new TimelineLite({
            onStart: () => {
                this.state = SimpleReelModelState.START;
                this.setReelSetBasedOnMode();
                this._stopSymbols = [];
                this._nudgeSteps = 0;
            },
            onComplete: () => {
                this.startSpin(reverse);
            }
        });

        const tween: gsap.core.Tween = this.step(reverse);
        tween.vars.ease = "back.in";
        tween.totalDuration(this.STEP_DURATION * 8);

        tl.add(tween);

        return tl;
    }

    public bounce(bounceFactor: number = 0.2, durationFactor: number = 1, reverse: boolean = false): TimelineLite {
        const tl = new TimelineLite();
        const overshoot = TweenMax.to(this.animatedStepData, this.STEP_DURATION * durationFactor, {
            y: reverse ? -this.stepSize * bounceFactor : this.stepSize * bounceFactor,
            ease: Power2.easeOut,
            onStart: () => {
                this.state = SimpleReelModelState.BOUNCE;
            },
            onUpdate: () => this.updatePosition(),
        });

        const back = TweenMax.to(this.animatedStepData, this.STEP_DURATION * durationFactor * 2, {
            y: 0,
            ease: Power2.easeOut,
            onUpdate: () => this.updatePosition(),
        });

        tl.add(overshoot);
        tl.add(back);

        return tl;
    }

    public nudge(reverse: boolean = false): gsap.core.Tween {
        const tween = this.step(reverse);
        tween.eventCallback("onStart", () => {
            this._nudgeSteps += reverse ? 1 : -1;
            this.state = SimpleReelModelState.NUDGE;
            this.onAnimationStart(tween, reverse);
        });

        return tween;
    }

    public getAllVisibleStopSymbols(): SimpleReelModelSymbolData [] {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this._nudgeSteps;
        return this._stopSymbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
    }

    public getStopSymbolAt(symbolIndex: number): SimpleReelModelSymbolData {
        return this._stopSymbols[symbolIndex + this.paddingConfig.visualPaddingBefore + this._nudgeSteps + this.paddingConfig.paddingBefore];
    }

    private setReelSetBasedOnMode(): void {
        if (Game.buyFeatureName === "GOD_MODE") {
            this.setReelSet(Config.XGOD_REELSET);
        } else if (Game.model.data.nextMode === GameFeatureMode.RESPIN) {
            this.setReelSet(Config.XMENTAL_RESPIN_REELSET[this.id]);
        } else if (Game.model.data.nextMode === GameFeatureMode.FREESPIN) {
            this.setReelSet(Config.FS_REEL_SET[this.id]);
        } else if (Game.model.data.nextMode === GameFeatureMode.FREESPIN_COOL) {
            this.setReelSet(Game.model.s2InReels[0] > -1 ? Config.CFS_REEL_SET_1ST[this.id] : Config.CFS_REEL_SET_2ND[this.id]);
        } else if (Game.model.data.nextMode === GameFeatureMode.FREESPIN_SUPER) {
            this.setReelSet(Config.SFS_REEL_SET[this.id]);
        } else {
            this.setReelSet(Config.DEFAULT_REEL_SET[this.id]);
        }
    }

    private onReelStateChange(data: SimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    private getNextSymbolName(nextSymbolPosition: number): string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        return this.reelSet[nextIndex];

    }

    private getNextSymbol(position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        if (this._stopping && this._stopSymbols.length > 0) {
            const symbol = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }
        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position, state)
    }

    private createNewSymbol(name: string, position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        const symbol: SimpleReelModelSymbolData = {
            name: name,
            positionable: this._reelView.createSymbol(name, state, {reelID: this.id, position: position}),
            reelPosition: position
        }
        return symbol;
    }

    private getRandomSymbolInReelSet(): string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        return this.reelSet[randomIndex];
    }


    private clearSymbols(symbols: SimpleReelModelSymbolData[]): void {
        let symbol;
        while (symbol = symbols.pop()) {
            this._reelView.removeSymbol(symbol, this.state);
        }
    }


    private getRandomSymbols(count: number): string[] {
        const result: string[] = [];
        for (let i: number = 0; i < count; i++) {
            result.push(this.getRandomSymbolInReelSet());
        }
        return result;
    }

    private startSpin(reverse: boolean = false) {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this.masterSpinTimeline = new TimelineLite();
        this.masterSpinTimeline.add(this.spin(reverse));
    }

    private spin(reverse: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                if (this._spinning) {
                    this.masterSpinTimeline?.add(this.spin(reverse));
                    //this.spin(reverse);
                }
            }
        })
        tl.add(this.step(reverse));
        return tl;
    }

    private step(reverse: boolean = false): gsap.core.Tween {
        const tween: gsap.core.Tween = gsap.to(this.animatedStepData, this.STEP_DURATION, {
            y: reverse ? -this.stepSize : this.stepSize,
            ease: "none",
            onStart: () => this.onAnimationStart(tween, reverse),
            onUpdate: () => this.updatePosition(),
            onComplete: () => this.onAnimationComplete(reverse)
        });
        return tween;
    }

    private onAnimationStart(tween: gsap.core.Tween, reverse: boolean = false) {
        this._currentSpinStep = tween;
        this.addSymbol(reverse);
    }

    private onAnimationComplete(reverse: boolean = false) {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this._stopTimeline?.play(0);
        }

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse: boolean) {
        const valueChange = reverse ? 1 : -1
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number) {
        if (value >= this._stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this._stopSymbols.length - 1;
        }
        this._stopIndex = value;
    }

    private setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(reverse: boolean) {
        let newSymbols = [];
        if (reverse) {
            const newSymbolPosition = this._symbols[this._symbols.length - 1].reelPosition + 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
            //newSymbols.push(this.createNewSymbol(this.getNextSymbolName(newSymbolPosition), newSymbolPosition, this.state));
        } else {
            const newSymbolPosition = this._symbols[0].reelPosition - 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
        }

        this._symbols = this._symbols.concat(newSymbols);
        this._symbols.sort((a: any, b: any) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        })

    }

    private removeSymbol() {
        for (let i: number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition >= this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter) {
                //Remove from bottom
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._reelView.removeSymbol(removedSymbol, this.state);


            }
            if (symbol.reelPosition < -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore) {
                //Remove from top
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._reelView.removeSymbol(removedSymbol, this.state);

            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            const basePos = this.stepSize * symbol.reelPosition;
            symbol.positionable.position.y = basePos + this.animatedStepData.y;
        }
    }

    get reelSet(): string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    get stopSymbols(): SimpleReelModelSymbolData[] {
        return this._stopSymbols;
    }

    get stopTriggerTl(): TimelineLite | undefined {
        return this._stopTriggerTl;
    }

    get stopTimeline(): TimelineLite | undefined {
        return this._stopTimeline;
    }

    get speed(): number {
        return this._speed;
    }

    set speed(value: number) {
        this._speed = value;
        this.animationTimeScale = value * this.STEP_DURATION;
        this.masterSpinTimeline?.timeScale(this.animationTimeScale);
    }

    get state(): SimpleReelModelState {
        return this._state;
    }

    set state(value: SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel: this.id, state: this._state});
        }
    }

    get symbols(): SimpleReelModelSymbolData[] {
        return this._symbols;
    }


}